import { Paper, Box, Typography } from "@mui/material";
import { useRef, useState, useCallback, useEffect } from "react";
import { PlannerMapPlace } from "src/types/interfaces";
import './InteractiveMapPlanner.scss';


interface LocationCardProps {
  location: PlannerMapPlace;
  isActive: boolean;
  onClick: () => void;
}

const getPlaceholderSVG = (locationName: string, id: string): string => {
  let hash = 0;
  for (let i = 0; i < (locationName + id).length; i++) {
    hash = (locationName + id).charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  const hue = Math.abs(hash % 360);
  const saturation = 60 + (Math.abs(hash) % 30);
  const lightness = 50 + (Math.abs(hash) % 20);
  const letter = locationName.charAt(0).toUpperCase() || '?';

  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="300" height="180" viewBox="0 0 300 180">
      <rect width="300" height="180" fill="hsl(${hue}, ${saturation}%, ${lightness}%)" />
      <text x="150" y="100" font-family="Arial, sans-serif" font-size="72" fill="white" text-anchor="middle" dominant-baseline="middle">${letter}</text>
    </svg>
  `)}`;
};


export const LocationCard: React.FC<LocationCardProps> = ({ location, isActive, onClick }) => {
  const imageUrl = location.photos && location.photos.length > 0 ? location.photos[0].url : getPlaceholderSVG((location.name || ("(Unknown)")), location.placeId);

  const descriptionScrollRef = useRef<HTMLDivElement>(null);
  const [scrollMaskStyle, setScrollMaskStyle] = useState('linear-gradient(to bottom, black 70%, transparent 100%)'); // Default: fade bottom

  const handleDescriptionScroll = useCallback(() => {
    const el = descriptionScrollRef.current;
    if (el) {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const tolerance = 5; // pixels to avoid floating point issues

      const atTop = scrollTop <= tolerance;
      const atBottom = scrollHeight - scrollTop - clientHeight <= tolerance;
      // Check if content is actually scrollable (more content than visible area)
      const canScroll = scrollHeight > clientHeight + tolerance;

      if (!canScroll) {
        setScrollMaskStyle('none');
      } else if (atTop && !atBottom) {
        setScrollMaskStyle('linear-gradient(to bottom, black 80%, transparent 100%)'); // Fade bottom only
      } else if (!atTop && atBottom) {
        setScrollMaskStyle('linear-gradient(to top, black 80%, transparent 100%)');     // Fade top only
      } else if (!atTop && !atBottom) {
        setScrollMaskStyle('linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)'); // Fade both
      } else { // Covers atTop && atBottom (content fits perfectly but was deemed scrollable, e.g. 1px diff)
        setScrollMaskStyle('none');
      }
    }
  }, []);

  useEffect(() => {
    const el = descriptionScrollRef.current;
    // Call once to set initial state
    handleDescriptionScroll();
    if (el) {
      el.addEventListener('scroll', handleDescriptionScroll);
      // Re-calculate on resize if the container's clientHeight might change
      const resizeObserver = new ResizeObserver(handleDescriptionScroll);
      resizeObserver.observe(el);

      return () => {
        el.removeEventListener('scroll', handleDescriptionScroll);
        resizeObserver.unobserve(el);
      };
    }
  }, [location.description, handleDescriptionScroll]); // Re-run if description changes or card resizes (via parent)

  return (
    <Paper
      className={`location-card-item ${isActive ? 'active' : ''}`}
      onClick={onClick}
      elevation={isActive ? 6 : 2}
      sx={{
        flex: '0 0 auto',
        width: { xs: '240px', sm: '270px', md: '300px' },
        maxHeight: { xs: '200px', md: '220px' },
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: '12px',
        marginRight: '12px',
        boxShadow: isActive ? '0 6px 16px rgba(0,0,0,0.2)' : '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s, border 0.2s',
        border: isActive ? (theme) => `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.14)',
        }
      }}
    >
      {location.sequence !== undefined && (
        <Box className="sequence-badge">
          {location.sequence}
        </Box>
      )}
      {location.time && (
        <Box className="time-badge">
          {location.time}
        </Box>
      )}

      <Box
        className="card-image"
        sx={{
          height: { xs: '90px', md: '110px' },
          flexShrink: 0,
          backgroundImage: `url("${imageUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'transform 0.3s ease',
          position: 'relative',
          color: 'common.white',
        }}
      >
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60%',
          backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.0) 100%)',
          zIndex: 1,
        }} />
        <Typography
          variant="subtitle2"
          className="location-title"
          title={location.name}
          sx={{
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            position: 'absolute',
            bottom: '8px',
            left: '10px',
            right: '10px',
            zIndex: 2,
            color: 'inherit',
          }}
        >
          {location.name}
        </Typography>
      </Box>

      <Box sx={{
        padding: '12px 10px 8px 10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}>
        <Box
          className="location-description-scroll-container"
          ref={descriptionScrollRef}
          sx={{
            maxHeight: '3.9em',
            overflowY: 'auto',
            flexGrow: 1,
            mb: 1,
            position: 'relative',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            maskImage: scrollMaskStyle,
            WebkitMaskImage: scrollMaskStyle,
          }}
        >
          <Typography variant="body2" className="location-description" title={location.description} sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: '1.3' }}>
            {location.description}
          </Typography>
        </Box>

        {location.duration && (
          <Typography variant="caption" className="location-duration" sx={{ color: '#00BCD4', fontWeight: '500', mt: 'auto' }}>
            {location.duration}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

